import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { useT } from "@ec2/i18n/react";
import type { MessageKey, MessageParams } from "@ec2/i18n";
import { useRouter } from "@tanstack/react-router";

/** Screen title row with a back affordance (sub-screens of a tab). */
export function ScreenHeader({ title, trailing }: { title: string; trailing?: ReactNode }) {
  const t = useT();
  const router = useRouter();
  return (
    <header className="flex items-center gap-2 px-4 pb-2 pt-4">
      <button
        type="button"
        onClick={() => router.history.back()}
        aria-label={t("ec2.common.back")}
        data-testid="ec-back"
        className="-ml-2 flex size-10 items-center justify-center rounded-full text-fg-secondary focus-visible:outline-2 focus-visible:outline-primary"
      >
        <ArrowLeft size={24} aria-hidden />
      </button>
      <h1 className="min-w-0 flex-1 truncate font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg">
        {title}
      </h1>
      {trailing}
    </header>
  );
}

/** Localized inline error line (never the raw server message). */
export function ErrorLine({
  error,
  className = "",
}: {
  error: { key: MessageKey; params?: MessageParams } | null;
  className?: string;
}) {
  const t = useT();
  if (!error) return null;
  return (
    <p role="alert" data-testid="ec-error" className={`text-[13px] font-medium text-danger ${className}`}>
      {t(error.key, error.params)}
    </p>
  );
}

export function LoadingLine({ className = "" }: { className?: string }) {
  const t = useT();
  return (
    <p role="status" data-testid="ec-loading" className={`px-4 py-8 text-center text-[13px] font-medium text-fg-muted ${className}`}>
      {t("ec2.common.loading")}
    </p>
  );
}
